import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';
import toSlug from 'utils/toSlug';
import { loadingStateType } from 'utils/ts/LoadingStateType';

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface Props {
  pageLoading: loadingStateType;
}

export const getHeadingAnchorId = (heading: HTMLElement): string => (
  heading.id || heading.querySelector<HTMLElement>('[id]')?.id || ''
);

export const getHeadingDocumentTop = (
  heading: HTMLElement,
  scrollY = typeof window === 'undefined' ? 0 : window.scrollY,
): number => heading.getBoundingClientRect().top + scrollY;


export const getScrollOffset = (): number => {
  const heading = document.querySelector('.l-static-page h2, .l-static-page h3');
  if (!heading) return 100;
  return parseInt(getComputedStyle(heading).scrollMarginTop, 10) || 100;
};

const OnThisPage: React.FC<Props> = ({ pageLoading }) => {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const observerCleanupRef = useRef<(() => void) | null>(null);
  const suppressUntilRef = useRef(0);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    setHeadings([]);
    setActiveId(null);
    observerCleanupRef.current?.();
    observerCleanupRef.current = null;

    if (pageLoading !== 'SUCCESS') {
      return;
    }

    const container = document.querySelector('.l-static-page');
    if (!container) {
      return;
    }

    let scanTimeout: ReturnType<typeof setTimeout> | null = null;
    let mutationObserver: MutationObserver | null = null;
    let scanned = false;

    const scan = () => {
      if (scanned) return;

      const elements = Array.from(container.querySelectorAll<HTMLElement>('h2, h3'));
      const results: Heading[] = [];
      const idSet = new Set<string>();
      let dupSuffix = 2;
      // Cache once per scan: the offset only changes if CSS changes.
      const scrollOffset = getScrollOffset();

      elements.forEach(el => {
        const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
        const level = parseInt(el.tagName.charAt(1), 10);

        // The element that actually carries the anchor id (the heading itself,
        // or a nested anchor), so deduplication rewrites the right node.
        const anchorEl = el.id ? el : el.querySelector<HTMLElement>('[id]');
        let id = getHeadingAnchorId(el);
        if (!id) {
          const slug = toSlug(text);
          if (!slug) {
            return;
          }
          id = slug;
          el.id = id;
        }

        // Deduplicate against ids already in the nav — including pre-existing
        // (CMS-authored) ids — so every nav entry has a unique target.
        let uniqueId = id;
        while (idSet.has(uniqueId)) {
          uniqueId = `${id}-${dupSuffix++}`;
        }
        if (uniqueId !== id) {
          (anchorEl || el).id = uniqueId;
        }

        idSet.add(uniqueId);
        results.push({ id: uniqueId, text, level });
      });

      if (results.length >= 1) {
        scanned = true;
        setHeadings(results);
        mutationObserver?.disconnect();

        observerCleanupRef.current?.();
        observerCleanupRef.current = null;

        if (typeof IntersectionObserver !== 'undefined') {
          const observer = new IntersectionObserver(
            entries => {
              if (Date.now() < suppressUntilRef.current) return;
              for (const entry of entries) {
                if (entry.isIntersecting) {
                  const id = getHeadingAnchorId(entry.target as HTMLElement);
                  if (id) {
                    setActiveId(id);
                  }
                  return;
                }
              }
              const bandTop = scrollOffset;
              const bandBottom = window.innerHeight * 0.6;
              let current: string | null = null;
              elements.forEach(el => {
                const top = el.getBoundingClientRect().top;
                if (!current && top >= bandTop && top < bandBottom) {
                  current = getHeadingAnchorId(el);
                }
              });
              setActiveId(current);
            },
            { rootMargin: `-${scrollOffset}px 0px -40% 0px` }
          );

          elements.forEach(el => {
            observer.observe(el);
          });

          observerCleanupRef.current = () => observer.disconnect();
        } else {
          const handleScroll = () => {
            if (Date.now() < suppressUntilRef.current) return;
            const scrollPos = window.scrollY + scrollOffset;
            let current: string | null = null;

            elements.forEach(el => {
              if (getHeadingDocumentTop(el) <= scrollPos) {
                current = getHeadingAnchorId(el);
              }
            });

            setActiveId(current);
          };

          window.addEventListener('scroll', handleScroll, { passive: true });
          handleScroll();

          observerCleanupRef.current = () => window.removeEventListener('scroll', handleScroll);
        }
      }
    };

    scanTimeout = setTimeout(scan, 0);

    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(mutations => {
        const hasNewHeadings = mutations.some(m =>
          Array.from(m.addedNodes).some(
            node =>
              node instanceof HTMLElement &&
              (node.matches('h2, h3') || node.querySelector('h2, h3'))
          )
        );
        if (hasNewHeadings) {
          if (scanTimeout) clearTimeout(scanTimeout);
          scanTimeout = setTimeout(scan, 50);
        }
      });

      mutationObserver.observe(container, { childList: true, subtree: true });
    }

    return () => {
      if (scanTimeout) clearTimeout(scanTimeout);
      mutationObserver?.disconnect();
      observerCleanupRef.current?.();
      observerCleanupRef.current = null;
    };
  }, [pageLoading]);

  const handleClick = (id: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      e.button !== 0
      || e.metaKey
      || e.ctrlKey
      || e.shiftKey
      || e.altKey
    ) {
      return;
    }

    const el = document.getElementById(id);
    if (!el) {
      return;
    }

    e.preventDefault();

    // Lock the clicked link as active immediately so the observer does not
    // flip to a different heading during the smooth-scroll animation.
    setActiveId(id);
    suppressUntilRef.current = Date.now() + 800;

    const prefersReducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    requestAnimationFrame(() => {
      el.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
      el.setAttribute('tabindex', '-1');
      // preventScroll so the browser's focus scroll does not hijack
      // the explicit scrollIntoView({ block: 'start' }) above.
      el.focus({ preventScroll: true });
      window.history.replaceState(null, '', `#${id}`);
    });
  };

  if (headings.length < 1) {
    return null;
  }

  return (
    <nav className="on-this-page" aria-label="On this page">
      <span className="on-this-page__label">ON THIS PAGE</span>
      <ul className="on-this-page__list">
        {headings.map(heading => (
          <li
            key={heading.id}
            className={`on-this-page__item on-this-page__item--h${heading.level}${activeId === heading.id ? ' on-this-page__item--is-active' : ''}`}
          >
            <a
              className="on-this-page__link"
              href={`#${heading.id}`}
              aria-current={activeId === heading.id ? 'location' : undefined}
              onClick={handleClick(heading.id)}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default connect(
  R.applySpec({
    pageLoading: R.pathOr(
      'NOT_LOADED',
      ['screens', 'info', 'page', 'loading', 'state'],
    ),
  })
)(OnThisPage);
